<template>
  <teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-[1000] pointer-events-auto flex items-end sm:items-start justify-center bg-black/40 p-0 sm:p-4 sm:pt-12"
      @click.self="$emit('close')"
    >
      <div
        class="bg-white dark:bg-gray-800 w-full h-[100dvh] sm:h-[85vh] rounded-none sm:rounded-lg shadow-lg overflow-hidden flex flex-col md:max-w-[95vw] lg:max-w-[85vw]"
        @click.stop
      >
        <div
          class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between"
        >
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Collega toevoegen</h3>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Periode: {{ formatDate(range.start) }} – {{ formatDate(range.end) }} ({{
                weeksHeaders.length
              }}
              weken)
            </p>
          </div>
          <button
            @mousedown.stop.prevent
            @click.stop="$emit('close')"
            class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            ✕
          </button>
        </div>

        <!-- Filters -->
        <AssignEmployeeFilterPanel
          ref="filterPanel"
          :filters="filters"
          v-model:dpw="dpw"
          v-model:availability-mode="availabilityMode"
          :teams="teams"
          :unique-functions="uniqueFunctions"
          :employees="employees"
        />

        <!-- Invalid date warning -->
        <div
          v-if="invalidProjectDates"
          class="mx-6 mt-3 mb-0 p-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded"
        >
          Let op: Einddatum ligt vóór begindatum voor dit project. Koppelen is uitgeschakeld.
        </div>

        <!-- Invalid DPW warning -->
        <div
          v-if="invalidDpw"
          class="mx-6 mt-3 mb-0 p-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded"
        >
          Let op: DPW moet groter zijn dan 0. Koppelen is uitgeschakeld.
        </div>

        <!-- Grid -->
        <AssignEmployeeGrid
          :employees="filtered"
          :weeks-headers="weeksHeaders"
          :dpw="dpw"
          :sort-by="sortBy"
          :sort-order="sortOrder"
          :disabled="invalidProjectDates || invalidDpw"
          :name-column-width="nameColumnWidth"
          :availability-mode="availabilityMode"
          @update:sort-by="sortBy = $event"
          @update:sort-order="sortOrder = $event"
          @select="$emit('select', $event)"
          @open-week="openWeek"
          @open-employee="openEmployeeDetails"
        />
      </div>
      <!-- Reusable week hours modal -->
      <WeekHoursModal
        :open="showHours && !!selectedEmployee && !!selectedWeek"
        :employee="selectedEmployee"
        :week="selectedWeek"
        :period-start="range.start"
        :period-end="range.end"
        :extra-proposed-hours-by-week="selectedEmployeeLocalProposedHoursByWeek"
        :extra-proposals="selectedEmployeeLocalProposals"
        @close="
          () => {
            showHours = false
            selectedEmployee = null
            selectedWeek = null
          }
        "
      />

      <!-- Employee details modal with higher z-index (above this modal) -->
      <div v-if="showEmployeeDetails && selectedEmployeeForDetails" class="fixed inset-0 z-[1100]">
        <EmployeeModal
          :employee="selectedEmployeeForDetails"
          :period-start="range.start"
          :period-end="range.end"
          @close="
            () => {
              showEmployeeDetails = false
              selectedEmployeeForDetails = null
            }
          "
        />
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { computed, ref, watch, nextTick, watchEffect } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { hoursService } from '@/services/hoursService.js'
import { teamService } from '@/services/employeeService.js'
import { useScrollLock } from '@/composables/ui/useScrollLock'
import WeekHoursModal from './WeekHoursModal.vue'
import EmployeeModal from '../../employee/EmployeeModal.vue'
import AssignEmployeeFilterPanel from './AssignEmployeeFilterPanel.vue'
import AssignEmployeeGrid from './AssignEmployeeGrid.vue'
import { SWP_EMPLOYEE_ID, isSWP } from '@/constants/swp.js'
import {
  indexLocalProposalsByEmployee,
  localProposalHoursForWeek,
  localOtherHoursForWeek
} from '@/utils/proposalAvailability.js'

const props = defineProps({
  open: { type: Boolean, default: false },
  project: { type: Object, default: () => ({}) },
  assignedEmployeeIds: { type: Array, default: () => [] },
  initialTeamId: { type: String, default: '' },
  initialFunction: { type: String, default: '' },
  initialDpw: { type: Number, default: 1.0 },
  // Optional period overrides (for preference-specific date ranges)
  periodStart: { type: String, default: null },
  periodEnd: { type: String, default: null },
  // Locally-staged proposals on OTHER roles in this prospect — used to
  // simulate availability/match/dot in voorgesteld mode before they're saved.
  localProposals: { type: Array, default: () => [] },
  // The preference this modal is opened for; its own local proposal must
  // not penalize candidates against themselves.
  currentPreferenceId: { type: [String, Number], default: null },
  // All preferences in the current prospect — used to label local proposals
  // (kans + rolnaam) in the per-week details modal.
  preferences: { type: Array, default: () => [] },
  // Initial availability mode when the modal opens.
  defaultAvailabilityMode: { type: String, default: 'presourcing' }
})
defineEmits(['close', 'select'])

const filterPanel = ref(null)
const filters = ref({ team_ids: [], functions: [], search: '' })
const sortBy = ref('match')
const sortOrder = ref('desc')
const dpw = ref(1.0)
const availabilityMode = ref(props.defaultAvailabilityMode || 'presourcing')

// Initialize filters when modal opens
watch(
  () => props.open,
  isOpen => {
    if (isOpen) {
      if (props.initialTeamId) {
        filters.value.team_ids = [props.initialTeamId]
      } else {
        filters.value.team_ids = []
      }
      if (props.initialFunction) {
        filters.value.functions = [props.initialFunction]
      } else {
        filters.value.functions = []
      }
      if (props.initialDpw && props.initialDpw > 0) {
        dpw.value = props.initialDpw
      }
      availabilityMode.value = props.defaultAvailabilityMode || 'presourcing'
      // Focus the search input via sub-component
      nextTick(() => {
        filterPanel.value?.focusSearch?.()
      })
    }
  },
  { immediate: true }
)

// Clamp DPW between 0 and 5.00 with 2 decimals
watch(dpw, val => {
  let n = Number(val)
  if (!isFinite(n)) n = 0.1
  if (n < 0) n = 0
  if (n > 5.0) n = 5.0
  n = Math.round(n * 100) / 100
  if (n !== dpw.value) dpw.value = n
})

const range = computed(() => {
  const start = props.periodStart || props.project?.start_date
  const end = props.periodEnd || props.project?.expected_closing_date
  const startD = start ? new Date(start) : new Date()
  let endD = end ? new Date(end) : null
  if (!endD || !start || endD <= startD) {
    endD = new Date(startD)
    endD.setMonth(endD.getMonth() + 4)
  }
  return { start: startD.toISOString().slice(0, 10), end: endD.toISOString().slice(0, 10) }
})

const invalidProjectDates = computed(() => {
  const s = props.periodStart || props.project?.start_date
  const e = props.periodEnd || props.project?.expected_closing_date
  if (!s || !e) return false
  return new Date(e) < new Date(s)
})

const invalidDpw = computed(() => {
  return Number(dpw.value) === 0
})

const { data: teams = [] } = useQuery({
  queryKey: ['teams'],
  queryFn: teamService.getAll,
  staleTime: 1000 * 60 * 10
})

const { data: availabilityData } = useQuery({
  enabled: computed(() => props.open),
  queryKey: computed(() => [
    'assign-availability',
    range.value.start,
    range.value.end,
    availabilityMode.value
  ]),
  queryFn: async () => {
    const params = {
      start_date: range.value.start,
      end_date: range.value.end
    }
    if (availabilityMode.value && availabilityMode.value !== 'presourcing') {
      params.availability_mode = availabilityMode.value
    }
    return await hoursService.getWeeklyAvailability(params)
  },
  staleTime: 1000 * 30
})

const employees = computed(() => availabilityData?.value?.data || [])

// Build per-preference metadata for labelling local proposals in the
// week-detail modal (kans + rolnaam).
const preferencesById = computed(() => {
  const map = new Map()
  for (const p of props.preferences || []) {
    if (p?.id != null) map.set(p.id, p)
  }
  return map
})

const localOrgName = computed(
  () => props.project?.organization_name || props.project?.subject || 'Deze klus'
)

const localRoleFor = preferenceId => {
  const pref = preferencesById.value.get(preferenceId)
  if (!pref) return null
  return pref.role || pref.experience_levels?.[0] || pref.teams?.[0] || null
}

// Adjusted employees: in voorgesteld mode, subtract local-staged proposals
// on OTHER preferences from each week's available_days, mark weeks that have
// a local proposal so the grid can render the "voorgesteld" dot, and append
// fictive proposed_assignments entries so the per-week detail modal can list
// them with kans + rolnaam. In other modes the data is passed through
// unchanged.
const employeesAdjusted = computed(() => {
  if (availabilityMode.value !== 'voorgesteld') return employees.value
  const idx = indexLocalProposalsByEmployee(props.localProposals, props.currentPreferenceId)
  if (idx.size === 0) return employees.value
  return employees.value.map(emp => {
    const empLocal = idx.get(emp.simplicate_id) || []
    if (empLocal.length === 0) return emp
    return {
      ...emp,
      weeks: (emp.weeks || []).map(w => {
        const localOtherHours = localOtherHoursForWeek(empLocal, w.week_start, w.week_end)
        if (localOtherHours <= 0) return w
        const baseAvailable = Number(w.available_days || 0)
        const adjustedAvailable = Math.max(0, baseAvailable - localOtherHours / 8)
        const localProposedExtras = empLocal
          .map(lp => {
            const hours = localProposalHoursForWeek(lp, w.week_start, w.week_end)
            if (hours <= 0) return null
            return {
              prospect_subject: localOrgName.value,
              organization_name: localOrgName.value,
              role: lp.role || localRoleFor(lp.preferenceId),
              dpw: Number(lp.dpw) || 0,
              hours_this_week: hours,
              preference_id: lp.preferenceId,
              is_local: true
            }
          })
          .filter(Boolean)
        return {
          ...w,
          available_days: adjustedAvailable,
          has_local_proposal: localProposedExtras.length > 0,
          proposed_assignments: [...(w.proposed_assignments || []), ...localProposedExtras]
        }
      })
    }
  })
})

const weeksHeaders = computed(() => {
  const first = employees.value[0]
  if (!first) return []
  return first.weeks.map(w => ({
    week_start: w.week_start,
    week_end: w.week_end,
    iso_label: w.iso_week
  }))
})

const uniqueFunctions = computed(() =>
  Array.from(new Set(employees.value.map(e => e.function).filter(Boolean)))
)

// Cache for team employee IDs (multiple teams)
const teamEmployeesCache = ref({})

watchEffect(async () => {
  const selectedTeamIds = filters.value.team_ids || []
  for (const teamId of selectedTeamIds) {
    if (!teamEmployeesCache.value[teamId]) {
      try {
        const list = await teamService.getEmployees(teamId)
        teamEmployeesCache.value[teamId] = (list || []).map(e => e.simplicate_id)
      } catch (e) {
        console.error(`Error fetching employees for team ${teamId}:`, e)
        teamEmployeesCache.value[teamId] = []
      }
    }
  }
})

const selectedTeamEmployeeIds = computed(() => {
  const selectedTeamIds = filters.value.team_ids || []
  if (selectedTeamIds.length === 0) return null

  const allIds = new Set()
  for (const teamId of selectedTeamIds) {
    const ids = teamEmployeesCache.value[teamId] || []
    ids.forEach(id => allIds.add(id))
  }
  return allIds
})

// Match percentage (needed here for sorting)
const matchPercent = emp => {
  if (emp.simplicate_id === SWP_EMPLOYEE_ID) return 0
  const weeks = weeksHeaders.value
  const required = Number(dpw.value)
  if (!weeks.length || !required) return 0
  let ok = 0
  for (const w of weeks) {
    const wk = emp.weeks.find(x => x.week_start === w.week_start)
    const val = wk ? Number(wk.available_days || 0) : 0
    if (val >= required) ok++
  }
  return (ok / weeks.length) * 100
}

const filtered = computed(() => {
  const s = (filters.value.search || '').toLowerCase()
  const selectedFunctions = filters.value.functions || []
  const teamIds = selectedTeamEmployeeIds.value
  const assignedIds = props.assignedEmployeeIds || []

  const result = employeesAdjusted.value.filter(e => {
    const matchesSearch = !s || e.name.toLowerCase().includes(s)
    const notAssigned = !assignedIds.includes(e.simplicate_id)

    if (isSWP(e)) return matchesSearch

    const matchesFunction =
      selectedFunctions.length === 0 ||
      selectedFunctions.some(f => (e.function || '').toLowerCase() === f.toLowerCase())
    const matchesTeam = teamIds === null || teamIds.has(e.simplicate_id)
    return matchesSearch && matchesFunction && matchesTeam && notAssigned
  })

  result.sort((a, b) => {
    let comparison = 0

    if (sortBy.value === 'name') {
      comparison = a.name.localeCompare(b.name)
    } else if (sortBy.value === 'match') {
      comparison = matchPercent(a) - matchPercent(b)
    }

    return sortOrder.value === 'asc' ? comparison : -comparison
  })

  return result
})

const formatDate = s => new Date(s + 'T00:00:00').toLocaleDateString('nl-NL')

// Calculate dynamic column widths
const modalAndColumnWidths = computed(() => {
  if (!employees.value || employees.value.length === 0) {
    return { modalWidth: '85vw', nameColumnWidth: '20vw' }
  }

  const weekCount = weeksHeaders.value.length || 8
  const modalWidthVw = 85
  const nameColumnVw = Math.max(8, Math.min(12, 15 - weekCount * 0.25))

  return {
    modalWidth: `${modalWidthVw}vw`,
    nameColumnWidth: `${nameColumnVw}vw`
  }
})

const nameColumnWidth = computed(() => modalAndColumnWidths.value.nameColumnWidth)

// Week hours modal state
const showHours = ref(false)
const selectedEmployee = ref(null)
const selectedWeek = ref(null)
const openWeek = (emp, week) => {
  selectedEmployee.value = emp
  selectedWeek.value = week
  showHours.value = true
}

// Extra proposed hours per week_start, used by the bar chart inside the
// week-hours modal so locally-staged proposals show up as the yellow bar.
const selectedEmployeeLocalProposedHoursByWeek = computed(() => {
  if (availabilityMode.value !== 'voorgesteld') return {}
  const emp = selectedEmployee.value
  if (!emp) return {}
  const idx = indexLocalProposalsByEmployee(props.localProposals, props.currentPreferenceId)
  const empLocal = idx.get(emp.simplicate_id) || []
  if (empLocal.length === 0) return {}
  const map = {}
  for (const w of emp.weeks || []) {
    const hours = localOtherHoursForWeek(empLocal, w.week_start, w.week_end)
    if (hours > 0) map[w.week_start] = hours
  }
  return map
})

// Locally-staged proposals for the selected employee, shaped like the
// server-side entries returned by getProposedByEmployee so they slot into
// the Aanvragen sidebar.
const selectedEmployeeLocalProposals = computed(() => {
  if (availabilityMode.value !== 'voorgesteld') return []
  const emp = selectedEmployee.value
  if (!emp) return []
  const idx = indexLocalProposalsByEmployee(props.localProposals, props.currentPreferenceId)
  const empLocal = idx.get(emp.simplicate_id) || []
  return empLocal.map((lp, i) => ({
    id: `local-${lp.preferenceId}-${i}`,
    prospect_subject: localOrgName.value,
    organization_name: localOrgName.value,
    role: lp.role || localRoleFor(lp.preferenceId),
    pl_selected_dpw: Number(lp.dpw) || 0,
    effective_start_date: lp.periodStart,
    effective_end_date: lp.periodEnd,
    is_local: true
  }))
})

// Employee details modal state
const showEmployeeDetails = ref(false)
const selectedEmployeeForDetails = ref(null)
const openEmployeeDetails = emp => {
  selectedEmployeeForDetails.value = emp
  showEmployeeDetails.value = true
}

// Scroll lock
const { lockScroll, unlockScroll } = useScrollLock()

watch(
  () => props.open,
  isOpen => {
    if (isOpen) {
      lockScroll()
    } else {
      unlockScroll()
    }
  }
)
</script>
